import { useState, useRef, useLayoutEffect } from 'react';
import { useColorModeValue } from '@chakra-ui/react';

const OK = '#22C55E', BAD = '#F87171', BRAND = '#7C5CFC';

/**
 * Đo bề rộng THẬT (px) của khung chứa để vẽ SVG theo tỉ lệ 1:1.
 * Trước đây viewBox cố định (W=1100) bị kéo width=100% -> trên màn lớn SVG phóng to,
 * cột/chữ/lưới dày lên trông thô. Dùng width thật -> mọi kích thước luôn đúng px, sắc nét.
 */
function useChartWidth(fallback = 900) {
  const ref = useRef(null);
  const [w, setW] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const cw = entries[0]?.contentRect?.width;
      if (cw && cw > 0) setW(Math.round(cw));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

function useAxis() {
  return {
    line: useColorModeValue('#EAE7F5', '#2A2350'),
    muted: useColorModeValue('#6E6A85', '#A79FC8'),
  };
}

/** Tooltip SVG bám theo điểm hover. lines: [{t, c?}] (c = màu chấm tuỳ chọn). */
function Tip({ x, y, W, lines }) {
  if (!lines) return null;
  const pad = 9, lh = 15, dot = 8;
  const hasDot = lines.some((l) => l.c);
  const textW = Math.max(...lines.map((l) => l.t.length)) * 6.4 + (hasDot ? dot + 6 : 0);
  const w = textW + pad * 2;
  const h = lines.length * lh + pad * 2 - 3;
  let bx = x - w / 2;
  bx = Math.max(2, Math.min(W - w - 2, bx));
  let by = y - h - 12;
  if (by < 2) by = y + 14;
  return (
    <g pointerEvents="none">
      <rect x={bx} y={by} width={w} height={h} rx="9" fill="#241D47" opacity="0.96" />
      {lines.map((l, i) => {
        const ty = by + pad + i * lh + 10;
        return (
          <g key={i}>
            {l.c && <circle cx={bx + pad + 4} cy={ty - 4} r="4" fill={l.c} />}
            <text x={bx + pad + (l.c ? dot + 6 : 0)} y={ty} fontSize="11.5" fontWeight="600" fill="#fff">{l.t}</text>
          </g>
        );
      })}
    </g>
  );
}

/** Cột Đúng/Sai theo ngày. data: [{day, correct, wrong}] */
export function BarsChart({ data = [] }) {
  const { line, muted } = useAxis();
  const [hi, setHi] = useState(-1);
  const [ref, W] = useChartWidth();
  const H = 250, pad = 44, base = H - 30;
  const rawMax = Math.max(6, ...data.map((d) => Math.max(d.correct, d.wrong)));
  const max = rawMax * 1.15;
  const gw = (W - pad - 10) / Math.max(1, data.length);
  const fracs = [0, 0.25, 0.5, 0.75, 1];
  return (
    <div ref={ref} style={{ width: '100%' }}>
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ display: 'block' }} onMouseLeave={() => setHi(-1)}>
      {fracs.map((f, i) => {
        const y = base - f * (base - 10);
        return (
          <g key={i}>
            <line x1={pad} y1={y} x2={W - 6} y2={y} stroke={line} strokeWidth="1" />
            <text x={pad - 6} y={y + 3} textAnchor="end" fontSize="11" fill={muted}>{Math.round(f * max)}</text>
          </g>
        );
      })}
      {data.map((d, i) => {
        const cx = pad + i * gw + gw / 2, bw = Math.min(18, gw * 0.34);
        const hc = (d.correct / max) * (base - 10), hw = (d.wrong / max) * (base - 10);
        return (
          <g key={i}>
            <rect x={cx - bw - 2} y={base - hc} width={bw} height={hc} rx="4" fill={OK} opacity={hi === -1 || hi === i ? 1 : 0.45} />
            <rect x={cx + 2} y={base - hw} width={bw} height={hw} rx="4" fill={BAD} opacity={hi === -1 || hi === i ? 1 : 0.45} />
            <text x={cx} y={base + 16} textAnchor="middle" fontSize="11" fill={muted}>{d.day}</text>
            <rect x={pad + i * gw} y="0" width={gw} height={base} fill="transparent" onMouseEnter={() => setHi(i)} />
          </g>
        );
      })}
      {hi >= 0 && data[hi] && (
        <Tip x={pad + hi * gw + gw / 2} y={base - (Math.max(data[hi].correct, data[hi].wrong) / max) * (base - 10)} W={W}
          lines={[{ t: data[hi].day }, { t: `Đúng ${data[hi].correct}`, c: OK }, { t: `Sai ${data[hi].wrong}`, c: BAD }]} />
      )}
    </svg>
    </div>
  );
}

/** Area phút học. data: [{day, minutes}] */
export function AreaChart({ data = [], tipLabel = 'phút' }) {
  const { line, muted } = useAxis();
  const [hi, setHi] = useState(-1);
  const [ref, W] = useChartWidth();
  const H = 250, pad = 44, base = H - 30;
  const max = Math.max(10, ...data.map((d) => d.minutes)) * 1.15;
  const n = Math.max(1, data.length - 1), gw = (W - pad - 16) / n;
  const pts = data.map((d, i) => [pad + i * gw, base - (d.minutes / max) * (base - 10)]);
  const dLine = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0]} ${p[1]}`).join(' ');
  const dArea = pts.length ? `${dLine} L${pts[pts.length - 1][0]} ${base} L${pts[0][0]} ${base} Z` : '';
  const fracs = [0, 0.33, 0.66, 1];
  const cw = (W - pad - 16) / Math.max(1, data.length);
  return (
    <div ref={ref} style={{ width: '100%' }}>
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ display: 'block' }} onMouseLeave={() => setHi(-1)}>
      <defs><linearGradient id="gArea" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={BRAND} stopOpacity="0.42" /><stop offset="100%" stopColor={BRAND} stopOpacity="0" />
      </linearGradient></defs>
      {fracs.map((f, i) => {
        const y = base - f * (base - 10);
        return (
          <g key={i}>
            <line x1={pad} y1={y} x2={W - 6} y2={y} stroke={line} strokeWidth="1" />
            <text x={pad - 6} y={y + 3} textAnchor="end" fontSize="11" fill={muted}>{Math.round(f * max)}</text>
          </g>
        );
      })}
      {dArea && <path d={dArea} fill="url(#gArea)" />}
      {dLine && <path d={dLine} fill="none" stroke={BRAND} strokeWidth="2.5" strokeLinejoin="round" />}
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p[0]} cy={p[1]} r={hi === i ? 5.5 : 4} fill="#fff" stroke={BRAND} strokeWidth="2" />
          <text x={p[0]} y={base + 16} textAnchor="middle" fontSize="11" fill={muted}>{data[i].day}</text>
          <rect x={p[0] - cw / 2} y="0" width={cw} height={base} fill="transparent" onMouseEnter={() => setHi(i)} />
        </g>
      ))}
      {hi >= 0 && data[hi] && <Tip x={pts[hi][0]} y={pts[hi][1]} W={W} lines={[{ t: data[hi].day }, { t: `${data[hi].minutes} ${tipLabel}`, c: BRAND }]} />}
    </svg>
    </div>
  );
}

/** Cột chuỗi câu đúng (xanh = mở máy, đỏ = làm lại). data: [{streak, unlocked}] */
export function StreakChart({ data = [] }) {
  const { line, muted } = useAxis();
  const [hi, setHi] = useState(-1);
  const [ref, W] = useChartWidth();
  const H = 230, pad = 40, base = H - 26;
  const max = Math.max(6, ...data.map((d) => d.streak)) * 1.1;
  const gw = (W - pad - 10) / Math.max(1, data.length);
  const fracs = [0, 0.33, 0.66, 1];
  return (
    <div ref={ref} style={{ width: '100%' }}>
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ display: 'block' }} onMouseLeave={() => setHi(-1)}>
      {fracs.map((f, i) => {
        const y = base - f * (base - 8);
        return (
          <g key={i}>
            <line x1={pad} y1={y} x2={W - 6} y2={y} stroke={line} strokeWidth="1" />
            <text x={pad - 6} y={y + 3} textAnchor="end" fontSize="11" fill={muted}>{Math.round(f * max)}</text>
          </g>
        );
      })}
      {data.map((d, i) => {
        const bw = gw * 0.62, x = pad + i * gw + (gw - bw) / 2, h = (d.streak / max) * (base - 8);
        return (
          <g key={i}>
            <rect x={x} y={base - h} width={bw} height={h} rx="3" fill={d.unlocked ? OK : BAD} opacity={hi === -1 || hi === i ? 1 : 0.5} />
            <rect x={pad + i * gw} y="0" width={gw} height={base} fill="transparent" onMouseEnter={() => setHi(i)} />
          </g>
        );
      })}
      {!data.length && <text x={W / 2} y={H / 2} textAnchor="middle" fontSize="13" fill={muted}>Chưa có dữ liệu chuỗi</text>}
      {hi >= 0 && data[hi] && (
        <Tip x={pad + hi * gw + gw / 2} y={base - (data[hi].streak / max) * (base - 8)} W={W}
          lines={[{ t: `${data[hi].streak} câu đúng`, c: data[hi].unlocked ? OK : BAD }, { t: data[hi].unlocked ? 'Mở máy ✅' : 'Làm lại ❌' }]} />
      )}
    </svg>
    </div>
  );
}

/** Xu hướng tổng câu đúng 7 ngày. data: [{day, correct}] */
export function TrendChart({ data = [] }) {
  const { line, muted } = useAxis();
  const [hi, setHi] = useState(-1);
  const [ref, W] = useChartWidth();
  const H = 300, pad = 44, base = H - 40;
  const max = Math.max(10, ...data.map((d) => d.correct)) * 1.2;
  const n = Math.max(1, data.length - 1), gw = (W - pad * 2) / n;
  const pts = data.map((d, i) => [pad + i * gw, base - (d.correct / max) * (base - 18)]);
  const dLine = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0]} ${p[1]}`).join(' ');
  const dArea = pts.length ? `${dLine} L${pts[pts.length - 1][0]} ${base} L${pts[0][0]} ${base} Z` : '';
  const grid = [0, 0.25, 0.5, 0.75, 1].map((f) => ({ y: base - f * (base - 18), v: Math.round(max * f) }));
  const cw = (W - pad * 2) / Math.max(1, data.length);
  return (
    <div ref={ref} style={{ width: '100%', padding: '10px 8px 4px' }}>
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ display: 'block' }} onMouseLeave={() => setHi(-1)}>
      <defs><linearGradient id="gTrend" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={BRAND} stopOpacity="0.42" /><stop offset="100%" stopColor={BRAND} stopOpacity="0" />
      </linearGradient></defs>
      {grid.map((g, i) => (
        <g key={i}>
          <line x1={pad} y1={g.y} x2={W - 10} y2={g.y} stroke={line} strokeWidth="1" />
          <text x={pad - 8} y={g.y + 4} textAnchor="end" fontSize="12" fill={muted}>{g.v}</text>
        </g>
      ))}
      {dArea && <path d={dArea} fill="url(#gTrend)" />}
      {dLine && <path d={dLine} fill="none" stroke={BRAND} strokeWidth="3.5" strokeLinejoin="round" strokeLinecap="round" />}
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p[0]} cy={p[1]} r={hi === i ? 8 : 5} fill="#fff" stroke={BRAND} strokeWidth="3" />
          <text x={p[0]} y={base + 24} textAnchor="middle" fontSize="13" fontWeight="600" fill={muted}>{data[i].day}</text>
          <rect x={p[0] - cw / 2} y="0" width={cw} height={base} fill="transparent" onMouseEnter={() => setHi(i)} />
        </g>
      ))}
      {hi >= 0 && data[hi] && <Tip x={pts[hi][0]} y={pts[hi][1]} W={W} lines={[{ t: data[hi].day }, { t: `${data[hi].correct} câu đúng`, c: BRAND }]} />}
    </svg>
    </div>
  );
}

/** Số câu trả lời theo giờ trong ngày. data: [{hour, count}] (chỉ những giờ có hoạt động). */
export function HourChart({ data = [] }) {
  const { line, muted } = useAxis();
  const [hi, setHi] = useState(-1);
  const [ref, W] = useChartWidth();
  const H = 230, pad = 40, base = H - 30;
  const rawMax = Math.max(4, ...data.map((d) => d.count));
  const max = rawMax * 1.15;
  const peak = data.reduce((m, d) => Math.max(m, d.count), 0);
  const gw = (W - pad - 10) / Math.max(1, data.length);
  const fracs = [0, 0.5, 1];
  return (
    <div ref={ref} style={{ width: '100%' }}>
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ display: 'block' }} onMouseLeave={() => setHi(-1)}>
      {fracs.map((f, i) => {
        const y = base - f * (base - 10);
        return (
          <g key={i}>
            <line x1={pad} y1={y} x2={W - 6} y2={y} stroke={line} strokeWidth="1" />
            <text x={pad - 6} y={y + 3} textAnchor="end" fontSize="11" fill={muted}>{Math.round(f * max)}</text>
          </g>
        );
      })}
      {data.map((d, i) => {
        const bw = Math.min(34, gw * 0.66), x = pad + i * gw + (gw - bw) / 2, h = (d.count / max) * (base - 10);
        const isPeak = d.count === peak && peak > 0;
        return (
          <g key={i}>
            <rect x={x} y={base - h} width={bw} height={h} rx="4" fill={isPeak ? BRAND : '#38BDF8'} opacity={hi === -1 || hi === i ? 1 : 0.45} />
            <text x={x + bw / 2} y={base + 16} textAnchor="middle" fontSize="11" fill={muted}>{String(d.hour).padStart(2, '0')}h</text>
            <rect x={pad + i * gw} y="0" width={gw} height={base} fill="transparent" onMouseEnter={() => setHi(i)} />
          </g>
        );
      })}
      {!data.length && <text x={W / 2} y={H / 2} textAnchor="middle" fontSize="13" fill={muted}>Chưa có dữ liệu</text>}
      {hi >= 0 && data[hi] && (
        <Tip x={pad + hi * gw + gw / 2} y={base - (data[hi].count / max) * (base - 10)} W={W}
          lines={[{ t: `${String(data[hi].hour).padStart(2, '0')}h – ${String(data[hi].hour).padStart(2, '0')}:59` }, { t: `${data[hi].count} câu`, c: BRAND }]} />
      )}
    </svg>
    </div>
  );
}

/** Donut tổng câu đúng / tổng câu trả lời. correct = số câu đúng, wrong = số câu sai. */
export function DonutChart({ correct = 0, wrong = 0 }) {
  const { muted } = useAxis();
  const track = useColorModeValue('#EFECFB', '#241D47');
  const textCol = useColorModeValue('#1B1636', '#F2EEFF');
  const total = correct + wrong;
  const pct = total ? Math.round((correct / total) * 100) : 0;
  const size = 200, c = size / 2, r = 76, sw = 26;
  const C = 2 * Math.PI * r;
  const okLen = total ? (correct / total) * C : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{ flex: '0 0 auto', maxWidth: '100%' }}>
        <g transform={`rotate(-90 ${c} ${c})`}>
          <circle cx={c} cy={c} r={r} fill="none" stroke={track} strokeWidth={sw} />
          {total > 0 && (
            <>
              <circle cx={c} cy={c} r={r} fill="none" stroke={OK} strokeWidth={sw}
                strokeDasharray={`${okLen} ${C - okLen}`} />
              <circle cx={c} cy={c} r={r} fill="none" stroke={BAD} strokeWidth={sw}
                strokeDasharray={`${C - okLen} ${okLen}`} strokeDashoffset={-okLen} />
            </>
          )}
        </g>
        <text x={c} y={c - 2} textAnchor="middle" fontSize="36" fontWeight="800" fill={textCol}>{pct}%</text>
        <text x={c} y={c + 22} textAnchor="middle" fontSize="12.5" fill={muted}>chính xác</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11, minWidth: 160, flex: 1 }}>
        <DonutLegend c={OK} label="Câu đúng" v={correct} pct={total ? Math.round((correct / total) * 100) : 0} />
        <DonutLegend c={BAD} label="Câu sai" v={wrong} pct={total ? Math.round((wrong / total) * 100) : 0} />
        <div style={{ borderTop: `1px solid ${track}`, paddingTop: 10, marginTop: 1, fontSize: 13, color: muted }}>
          Tổng <b style={{ color: textCol }}>{total.toLocaleString('vi-VN')}</b> câu trả lời
        </div>
      </div>
    </div>
  );
}
function DonutLegend({ c, label, v, pct }) {
  const { muted } = useAxis();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
      <span style={{ width: 12, height: 12, borderRadius: 4, background: c, flex: '0 0 auto' }} />
      <span style={{ fontWeight: 700, fontSize: 13.5 }}>{label}</span>
      <span style={{ marginLeft: 'auto', fontWeight: 800, fontSize: 14 }}>{v.toLocaleString('vi-VN')}</span>
      <span style={{ color: muted, fontSize: 12.5, minWidth: 40, textAlign: 'right' }}>{pct}%</span>
    </div>
  );
}

/** Thanh ngang tỉ lệ đúng theo hạng mục. rows: [{label, pct, total, correct}]. Dùng cho "theo độ khó" & "theo chủ đề". */
export function AccuracyBars({ rows = [] }) {
  const { muted } = useAxis();
  const track = useColorModeValue('#EFECFB', '#241D47');
  const color = (pct) => (pct >= 85 ? OK : pct >= 65 ? '#F59E0B' : BAD);
  if (!rows.length) return <div style={{ color: muted, fontSize: 13, padding: '8px 2px' }}>Chưa có dữ liệu</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      {rows.map((r, i) => (
        <div key={i}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 4, gap: 10 }}>
            <span style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</span>
            <span style={{ color: muted, whiteSpace: 'nowrap', fontWeight: 700 }}>
              <span style={{ color: color(r.pct) }}>{r.pct}%</span> · {r.correct}/{r.total}
            </span>
          </div>
          <div style={{ height: 9, borderRadius: 99, background: track, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${r.pct}%`, background: color(r.pct), borderRadius: 99, transition: 'width .3s' }} />
          </div>
        </div>
      ))}
    </div>
  );
}
